/**
 * /operations/travel/reconciliation — Session 3B PR 19.
 * Owning agent: Rawatib. Returned travelers + expense reconciliation.
 */

import { useQuery } from "@tanstack/react-query";
import { AlertOctagon, CheckCircle2, Plus, Receipt } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { ReconciliationItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

function _statusToneClass(s: ReconciliationItem["status"]): string {
  if (s === "approved") return "bg-emerald-100 text-emerald-800";
  if (s === "recovery") return "bg-rose-100 text-rose-800";
  if (s === "needs_documentation") return "bg-amber-100 text-amber-800";
  return "bg-slate-100 text-slate-700";
}

function _fmtSar(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function TravelReconciliationPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();

  const list = useQuery({
    queryKey: ["session3b-travel-reconciliation", legalEntityId],
    queryFn: () => session3bApi.listReconciliation(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: ReconciliationItem[] = list.data?.items ?? [];

  const counts = useMemo(() => {
    const pending = items.filter((r) => r.status === "pending");
    const overdue = items.filter((r) => r.days_since_return > 14 && r.status !== "approved");
    const sumUnrecovered = pending.reduce(
      (a, r) => a + Math.max(0, r.variance_sar),
      0,
    );
    return {
      total: items.length,
      attention: pending.length,
      critical: overdue.length,
      sumUnrecovered,
      first_overdue: overdue[0],
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("travel.reconciliation.title", "Travel Reconciliation")}
          subtitle={t(
            "travel.reconciliation.subtitle",
            "Returned travelers with advance-vs-actual variance. Rawatib watches for overdue >14d reconciliations.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-text-tertiary">
            {t(
              "travel.reconciliation.hint",
              "Default per M3X.TRAVEL: 70% advance · 30% reconciliation. Variance > 20% prompts a receipt audit.",
            )}
          </p>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "travel.reconciliation.cta.submit_note",
                  "Submit Reconciliation on behalf — drawer with OCR receipt upload (Sonnet) per M3X.TRAVEL.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {t("travel.reconciliation.cta.submit", "Submit Reconciliation on behalf")}
          </button>
        </div>

        {counts.critical > 0 && counts.first_overdue && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<AlertOctagon className="h-4 w-4" />}
            severity="critical"
            headline={t(
              "travel.reconciliation.suggestion.overdue_headline",
              `${counts.critical} reconciliation${counts.critical === 1 ? "" : "s"} overdue >14 days · total advance unrecovered: SAR ${_fmtSar(counts.sumUnrecovered)}.`,
            )}
            body={t(
              "travel.reconciliation.suggestion.overdue_body",
              `Oldest: ${counts.first_overdue.employee_name_en} returned ${counts.first_overdue.days_since_return} days ago. Rawatib suggests escalating or marking recoverable from salary.`,
            )}
            primaryAction={{
              label: t("travel.reconciliation.suggestion.open_first", "Open first overdue"),
              onClick: () =>
                window.location.assign(
                  `/people/employees/${counts.first_overdue!.employee_id}`,
                ),
            }}
            suggestionKey={`travel-reconcile-overdue-${counts.first_overdue.trip_id}`}
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<CheckCircle2 className="h-4 w-4" />}
            severity="info"
            headline={t(
              "travel.reconciliation.suggestion.empty_headline",
              "No pending reconciliations.",
            )}
            body={t(
              "travel.reconciliation.suggestion.empty_body",
              "Rawatib notes: trips appear here when a traveler returns. Until then, see Active Travelers for the in-flight roster.",
            )}
            primaryAction={{
              label: t("travel.reconciliation.suggestion.active", "Active travelers"),
              onClick: () => window.location.assign("/operations/travel/active-travelers"),
            }}
            suggestionKey="travel-reconcile-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("travel.reconciliation.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("travel.reconciliation.col.destination", "Destination")}</th>
                  <th className="px-3 py-2">{t("travel.reconciliation.col.advance", "Advance (SAR)")}</th>
                  <th className="px-3 py-2">{t("travel.reconciliation.col.claimed", "Claimed (SAR)")}</th>
                  <th className="px-3 py-2">{t("travel.reconciliation.col.variance", "Variance")}</th>
                  <th className="px-3 py-2">{t("travel.reconciliation.col.days", "Days since return")}</th>
                  <th className="px-3 py-2">{t("travel.reconciliation.col.status", "Status")}</th>
                  <th className="px-3 py-2">{t("travel.reconciliation.col.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.trip_id} className="group border-t border-border-subtle hover:bg-surface-sunken/40">
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">{row.destination}</td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "travel.reconciliation.trace.advance",
                              `advance_paid_sar = M3X.TRAVEL advance% × per-diem total. Standard ratio: 70/30 (70% upfront, 30% on reconciliation).`,
                            ),
                          )
                        }
                      >
                        <span className="font-mono text-xs">{_fmtSar(row.advance_paid_sar)}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{_fmtSar(row.expenses_claimed_sar)}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`font-mono text-xs ${
                          row.variance_pct > 20
                            ? "font-semibold text-rose-700"
                            : row.variance_pct < 0
                              ? "font-semibold text-amber-700"
                              : "text-text-secondary"
                        }`}
                      >
                        {_fmtSar(row.variance_sar)} ({row.variance_pct.toFixed(1)}%)
                      </span>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">
                      <span
                        className={
                          row.days_since_return > 14 ? "font-semibold text-rose-700" : ""
                        }
                      >
                        {row.days_since_return}d
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_statusToneClass(row.status)}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {row.status !== "approved" && (
                        <div className="flex items-center gap-1.5 opacity-50 transition-opacity group-hover:opacity-100">
                          <button
                            type="button"
                            onClick={() =>
                              showAlertTrace(
                                t(
                                  "travel.reconciliation.action.approve_note",
                                  "Approve reconciliation — finalizes the trip and queues the 30% balance for off-cycle disbursement.",
                                ),
                              )
                            }
                            className="rounded border border-emerald-300 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50"
                          >
                            <CheckCircle2 className="me-1 inline h-3 w-3" />
                            {t("travel.reconciliation.action.approve", "Approve")}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              showAlertTrace(
                                t(
                                  "travel.reconciliation.action.request_docs_note",
                                  "Request more documentation — Khidmat WhatsApps the traveler for receipts.",
                                ),
                              )
                            }
                            className="rounded border border-amber-300 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 hover:bg-amber-50"
                          >
                            <Receipt className="me-1 inline h-3 w-3" />
                            {t("travel.reconciliation.action.request_docs", "Request docs")}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="rawatib"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: counts.critical,
            top_kind: t("travel.reconciliation.title", "Travel Reconciliation"),
            window_label: `unrecovered SAR ${_fmtSar(counts.sumUnrecovered)}`,
          }}
        />
      </aside>
    </div>
  );
}
